import { useMemo } from 'react';
import { useQuery } from 'react-query';
import type { PrimaryCountryISO } from '@/constants/countries';
import fetchIndustries, {
  GET_INDUSTRY_QUERY_KEY,
  Industry,
  IndustryResponse,
  MINIMUM_COMPANIES,
} from '@/pages/Market/query/fetchIndustries';

export interface Industries {
  sector: Record<string, Industry>;
  primary: Record<string, Industry>;
  secondary: Record<string, Industry>;
  tertiary: Record<string, Industry>;
}

const createIndustryByLevel = (
  industries: Array<IndustryResponse>,
  level: number,
  filterCompanyCount: boolean
) =>
  industries
    .filter(
      (l) =>
        l.level === level &&
        (filterCompanyCount ? l.companyCount >= MINIMUM_COMPANIES : true)
    )
    .map((v) => ({
      label: v.friendlyName || v.name,
      companyCount: v.companyCount,
      slug: v.slug,
      children: v.children.map((i) => i.id),
      id: v.id,
    }))
    .reduce((a, { id, ...rest }) => ({ ...a, [id]: rest }), {});

const useIndustriesFetch = (
  market: PrimaryCountryISO = 'us',
  filterCompanyCount = true,
  enabled = true
): { industries: Industries; isIndustriesFetched: boolean } => {
  const { data: response = { Industries: [] }, isFetched } = useQuery(
    [GET_INDUSTRY_QUERY_KEY, market],
    fetchIndustries,
    {
      staleTime: Infinity,
      keepPreviousData: true,
      refetchOnMount: false,
      enabled: enabled,
    }
  );

  const industries = useMemo((): Industries => {
    const sector: Record<string, Industry> = createIndustryByLevel(
      response.Industries,
      1,
      filterCompanyCount
    );
    const primary: Record<string, Industry> = createIndustryByLevel(
      response.Industries,
      2,
      filterCompanyCount
    );
    const secondary: Record<string, Industry> = createIndustryByLevel(
      response.Industries,
      3,
      filterCompanyCount
    );
    const tertiary: Record<string, Industry> = createIndustryByLevel(
      response.Industries,
      4,
      filterCompanyCount
    );
    return {
      sector,
      primary,
      secondary,
      tertiary,
    };
  }, [response, filterCompanyCount]);

  return {
    industries,
    isIndustriesFetched: isFetched,
  };
};

export default useIndustriesFetch;
